import { useState } from "react";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DataState,
  Input,
  Modal,
  PageHeader,
  Table,
  useToast,
  type Column,
} from "@vms/ui-web";
import { useApi, useCanIDo } from "../auth/AuthProvider";
import { useOrgContext } from "../context/OrgContext";
import { useQuery } from "../api/queries";

const ALL_SCOPES = [
  "bookings:write",
  "bookings:read",
  "bookings:hold",
  "drivers:read",
  "drivers:write",
  "leaderboards:read",
];

interface KeyRow {
  id: string;
  name: string;
  scopes: string[];
  lastUsedAt?: string | null;
  revokedAt?: string | null;
  createdAt: string;
}

export function PartnerKeys() {
  const { currentOrg } = useOrgContext();
  const api = useApi();
  const canIDo = useCanIDo();
  const { addToast } = useToast();

  const keys = useQuery<KeyRow[]>(
    currentOrg ? `/api/organisations/${currentOrg.id}/partner-keys` : null,
  );
  const [creating, setCreating] = useState(false);
  const [revealKey, setRevealKey] = useState<{ name: string; key: string } | null>(null);
  const [revoking, setRevoking] = useState<KeyRow | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  if (!currentOrg) return null;
  const canManage = canIDo("partners:manage", currentOrg.id);

  async function revoke(key: KeyRow) {
    setBusyId(key.id);
    try {
      const res = await api(
        `/api/organisations/${currentOrg!.id}/partner-keys/${key.id}/revoke`,
        { method: "POST" },
      );
      const json = await res.json();
      if (!res.ok || !json.success) {
        addToast(json.error ?? "Failed to revoke", "error");
        return;
      }
      addToast(`${key.name} revoked`);
      keys.refetch();
    } finally {
      setBusyId(null);
      setRevoking(null);
    }
  }

  const columns: Column<KeyRow>[] = [
    {
      key: "name",
      header: "Name",
      render: (k) => <span className="font-medium text-gray-900">{k.name}</span>,
    },
    {
      key: "scopes",
      header: "Scopes",
      render: (k) => (
        <div className="flex flex-wrap gap-1">
          {k.scopes.map((s) => (
            <Badge key={s} className="bg-gray-100 text-gray-700">
              {s}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      key: "lastUsedAt",
      header: "Last used",
      render: (k) => (
        <span className="text-sm text-gray-500">
          {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : "Never"}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (k) =>
        k.revokedAt ? (
          <Badge className="bg-red-100 text-red-700">Revoked</Badge>
        ) : (
          <Badge className="bg-green-100 text-green-700">Active</Badge>
        ),
    },
    {
      key: "actions",
      header: "",
      render: (k) =>
        canManage && !k.revokedAt ? (
          <div className="text-right">
            <Button
              size="sm"
              variant="danger"
              loading={busyId === k.id}
              onClick={() => setRevoking(k)}
            >
              Revoke
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Partner API keys"
        subtitle={`Inbound integrations into ${currentOrg.name}.`}
      >
        {canManage && <Button onClick={() => setCreating(true)}>New key</Button>}
      </PageHeader>

      <Card className="overflow-hidden">
        <DataState
          loading={keys.loading}
          error={keys.error}
          empty={!keys.loading && (keys.data ?? []).length === 0}
          emptyMessage="No partner keys yet."
        >
          <Table<KeyRow> columns={columns} data={keys.data ?? []} keyExtractor={(k) => k.id} />
        </DataState>
      </Card>

      <CreateKeyModal
        open={creating}
        onClose={() => setCreating(false)}
        orgId={currentOrg.id}
        onCreated={(name, key) => {
          keys.refetch();
          setRevealKey({ name, key });
        }}
      />

      <Modal
        isOpen={!!revealKey}
        onClose={() => setRevealKey(null)}
        title="API key — copy now"
      >
        {revealKey && (
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              This is the only time you'll see <span className="font-medium">{revealKey.name}</span>'s
              plaintext value. Copy it to your password manager and share via a secure
              channel — re-issuing means rotating the integration on the partner side.
            </div>
            <pre className="rounded bg-gray-50 border border-gray-200 p-3 text-xs overflow-x-auto">
              {revealKey.key}
            </pre>
            <div className="flex justify-end">
              <Button onClick={() => setRevealKey(null)}>Done</Button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={!!revoking}
        title="Revoke API key?"
        message={
          revoking
            ? `Revoke ${revoking.name}? Any existing integrations using it will start failing immediately.`
            : ""
        }
        confirmLabel="Revoke"
        loading={!!revoking && busyId === revoking.id}
        onClose={() => setRevoking(null)}
        onConfirm={() => revoking && revoke(revoking)}
      />
    </div>
  );
}

function CreateKeyModal({
  open,
  onClose,
  orgId,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  onCreated: (name: string, plaintext: string) => void;
}) {
  const api = useApi();
  const { addToast } = useToast();
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<Set<string>>(new Set(["bookings:write"]));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setName("");
    setScopes(new Set(["bookings:write"]));
    setError(null);
  }

  function toggleScope(s: string) {
    const next = new Set(scopes);
    if (next.has(s)) next.delete(s);
    else next.add(s);
    setScopes(next);
  }

  async function submit() {
    if (!name.trim() || scopes.size === 0) {
      setError("Name and at least one scope are required");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await api(`/api/organisations/${orgId}/partner-keys`, {
        method: "POST",
        body: JSON.stringify({ name: name.trim(), scopes: Array.from(scopes) }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        setError(json.error ?? "Failed to create key");
        return;
      }
      addToast("Partner key created");
      onCreated(json.data.name, json.data.key);
      reset();
      onClose();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal isOpen={open} onClose={() => { reset(); onClose(); }} title="New partner API key">
      <div className="space-y-4">
        <Input
          label="Name"
          placeholder="e.g. Acme Tours website"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
        <div>
          <div className="text-sm font-medium text-gray-700 mb-1">Scopes</div>
          <div className="grid grid-cols-2 gap-2">
            {ALL_SCOPES.map((s) => (
              <label key={s} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={scopes.has(s)}
                  onChange={() => toggleScope(s)}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <code className="text-xs">{s}</code>
              </label>
            ))}
          </div>
        </div>
        {error && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={() => { reset(); onClose(); }}>
            Cancel
          </Button>
          <Button onClick={submit} loading={submitting}>
            Create
          </Button>
        </div>
      </div>
    </Modal>
  );
}
